import { useMemo } from "react";
import { VehicleTripSegment } from "../../models/vehicle/vehicleTripSegment";
import { Position } from "../../components/framework/map/map";
import { useAppSelector } from "../../framework/customStore";
import { WorkShiftTimeSlotItem } from "../../models/workShitTimeSlot/workShiftTimeSlotItem";
import { VehiclePositionInfo } from "../../models/vehicle/vehiclePosition";
import {
    getWorkTimeTypeColorCode,
    getWorkTimeTypeColorClass,
    getWorkTimeTypeContrastColorClass,
} from "../../models/workShitTimeSlot/workTimeType";
import { Base } from "../../framework/base";

const findMatchingSlot = (
    segment: VehicleTripSegment,
    slots: WorkShiftTimeSlotItem[]
): WorkShiftTimeSlotItem | undefined => {
    const segStart = new Date(segment.startTime).getTime();
    return slots.find((slot) => {
        const slotStart = new Date(slot.startDate).getTime();
        const slotEnd = slot.endDate
            ? new Date(slot.endDate).getTime()
            : Date.now();
        return segStart >= slotStart && segStart < slotEnd;
    });
};

const findClosestPosition = (
    targetTime: string,
    allPositions: VehiclePositionInfo[]
): VehiclePositionInfo | undefined => {
    if (!allPositions.length) return undefined;
    const target = new Date(targetTime).getTime();
    let closest = allPositions[0];
    let minDiff = Math.abs(new Date(closest.timestamp).getTime() - target);
    for (let i = 1; i < allPositions.length; i++) {
        const diff = Math.abs(
            new Date(allPositions[i].timestamp).getTime() - target
        );
        if (diff < minDiff) {
            minDiff = diff;
            closest = allPositions[i];
        }
    }
    return closest;
};

export const useVehicleRouteMapData = (
    vehicleRoute: VehicleTripSegment[],
    workShiftTimeSlots?: WorkShiftTimeSlotItem[],
    selectedSlotId?: string,
    hoveredSlotId?: string
) => {
    const selectedVehicleId = useAppSelector((state) => state.monitorView.selectedVehicleId);
    const selectedRouteId = useAppSelector((state) => state.monitorView.selectedRouteId);
    const hoveredRouteId = useAppSelector((state) => state.monitorView.hoveredRouteId);
    const slotAddresses = useAppSelector((state) => state.monitorView.vehicleShiftSlotAddresses);

    const sortedSlots = useMemo(
        () =>
            workShiftTimeSlots
                ?.slice()
                .sort((a, b) => Base.dayjsCompare(a.startDate, b.startDate)),
        [workShiftTimeSlots]
    );

    const allPositions = useMemo(
        () =>
            vehicleRoute?.flatMap((segment) => segment.positions) ?? [],
        [vehicleRoute]
    );

    const routes = useMemo(() => {
        return vehicleRoute?.map((segment, index) => {
            const uniqueId = `${selectedVehicleId}-${index}`; // Ensure IDs match the logic in VehicleRouteList
            const isHighlighted = uniqueId === selectedRouteId || uniqueId === hoveredRouteId;

            const coords: Position[] = segment.positions.map((position) => ({
                lat: position.latitude,
                lng: position.longitude,
            }));

            const matchingSlot = sortedSlots?.length
                ? findMatchingSlot(segment, sortedSlots)
                : undefined;

            const slotHighlighted = matchingSlot
                ? matchingSlot.id === selectedSlotId ||
                  matchingSlot.id === hoveredSlotId
                : false;
            const slotSelected = matchingSlot?.id === selectedSlotId;
            const slotHovered = matchingSlot?.id === hoveredSlotId;

            return {
                coords,
                highlight: isHighlighted || slotHighlighted,
                animated: true,
                ...(matchingSlot && {
                    dataId: matchingSlot.id,
                    strokeColor: getWorkTimeTypeColorCode(
                        matchingSlot.workTimeTypeType
                    ),
                    ...(slotHighlighted && {
                        strokeWeight: slotSelected ? 4 : slotHovered ? 7 : 3,
                        zIndex: slotSelected ? 2 : slotHovered ? 3 : 1,
                        strokeOpacity: 1,
                    }),
                }),
            };
        });
    }, [vehicleRoute, selectedRouteId, hoveredRouteId, sortedSlots, selectedSlotId, hoveredSlotId]);

    const points = useMemo(() => {
        // When work shift time slots are provided, show work type transition points
        if (sortedSlots?.length && allPositions.length) {
            return sortedSlots
                .map((slot) => {
                    const position = findClosestPosition(
                        slot.startDate,
                        allPositions
                    );
                    if (!position) return null;

                    const timeStr =
                        Base.dateStrToOriginalTimezoneTimeStr(slot.startDate);
                    const address = slotAddresses[slot.id];

                    return {
                        id: slot.id,
                        coords: {
                            lat: position.latitude,
                            lng: position.longitude,
                        },
                        text: [timeStr, slot.workTimeTypeName, slot.employeeName]
                            .filter(Boolean)
                            .join(" - "),
                        fullText: [
                            timeStr,
                            slot.workTimeTypeName,
                            slot.employeeName,
                            address,
                        ]
                            .filter(Boolean)
                            .join(" - "),
                        textColorClass: getWorkTimeTypeContrastColorClass(
                            slot.workTimeTypeType
                        ),
                        textBgColorClass: getWorkTimeTypeColorClass(
                            slot.workTimeTypeType
                        ),
                        groupColor: getWorkTimeTypeColorCode(
                            slot.workTimeTypeType
                        ),
                        groupName: slot.workTimeTypeName,
                        highlight:
                            slot.id === selectedSlotId ||
                            slot.id === hoveredSlotId,
                    };
                })
                .filter(Boolean);
        }

        // Default: show stopped segment points
        return vehicleRoute
            ?.filter((segment) => segment.state === "Stopped")
            .map((segment, index) => {
                const position = segment.positions[0];
                const stopNumber = index + 1;

                return {
                    id: `${position.vehicleId}-${position.timestamp}`,
                    coords: {
                        lat: position.latitude,
                        lng: position.longitude,
                    },
                    text: `# ${stopNumber}`,
                    fullText: [
                        `# ${stopNumber}`,
                        segment?.streetAddress,
                    ]
                        .filter(Boolean)
                        .join(" - "),
                    textColorClass: "white",
                    textBgColorClass: "blue-light",
                    groupColor: "#426ac6",
                    groupName: "stopped",
                };
            });
    }, [vehicleRoute, sortedSlots, allPositions, selectedSlotId, hoveredSlotId, slotAddresses]);

    return { routes, points };
};
